import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MAX_PACKAGE_BATCH,
  PACKAGE_CONDITIONS,
  PACKAGE_CONDITION_LABELS,
  type CreatePackageItem,
  type CreatePackageResult,
  type Page,
  type Resident,
  type StorageLocation,
  type Unit,
} from '@azko/shared';
import { ApiRequestError, api } from '../lib/api.js';
import { decodeScan, describeScan } from '../lib/scanner/carriers.js';
import { parseLabelText } from '../lib/scanner/parseLabel.js';
import { matchLabel, type UnitSuggestion } from '../lib/scanner/matchUnit.js';
import { useLabelOcr } from '../lib/scanner/useLabelOcr.js';
import { useBlocks } from '../lib/useBlocks.js';
import { PackagePhoto } from '../components/PackagePhoto.js';
import { CameraScanner } from '../components/CameraScanner.js';
import {
  Alert,
  Button,
  Card,
  Field,
  PageHeader,
  PlanBlock,
  Select,
  TextInput,
  cx,
} from '../components/ui.js';
import { IconBarcode, IconCheck, IconClose } from '../components/icons.js';

/**
 * Registro de entrada.
 *
 * O leitor e o primeiro passo da tela, e nao um extra escondido: a digitacao
 * de unidade, transportadora e rastreio com o pacote na mao e o gargalo real
 * da portaria. A camera le o codigo de barras (rastreio exato) e o texto da
 * etiqueta (destino aproximado), e o formulario abaixo ja chega preenchido.
 *
 * Nada e gravado sem o porteiro ver: todo campo vindo do leitor fica marcado
 * como automatico e continua editavel. Quando a leitura e ambigua — o mesmo
 * numero de apartamento em blocos diferentes — a tela PERGUNTA em vez de
 * escolher, porque campo preenchido errado engana mais do que campo vazio.
 *
 * A previa da notificacao fica ao lado de proposito: o porteiro ve exatamente
 * o que o morador vai receber antes de confirmar.
 *
 * Bloco e um campo proprio, e nao um prefixo no nome da unidade: em predio com
 * blocos o numero 101 existe em todos eles, e uma lista unica de "A • 101, B •
 * 101, C • 101" e justamente onde o porteiro erra com o pacote na mao.
 * Escolher o bloco primeiro corta a lista para o tamanho de um andar.
 */

/** Marca visual dos campos que vieram da camera. */
function AutoBadge({ onClear }: { onClear: () => void }) {
  return (
    <span className="ml-2 inline-flex items-center gap-1 rounded-pill border border-accent/40 bg-accent/10 px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-wider text-accent">
      <IconCheck className="size-3" />
      auto
      <button
        type="button"
        // O selo vive dentro do <label>; sem o preventDefault o clique aqui
        // tambem focaria o campo que acabou de ser limpo.
        onClick={(event) => {
          event.preventDefault();
          onClear();
        }}
        aria-label="Limpar campo preenchido pela camera"
        className="-mr-0.5 rounded-full p-0.5 hover:bg-accent/20"
      >
        <IconClose className="size-3" />
      </button>
    </span>
  );
}

type AutoField = 'unitId' | 'carrier' | 'trackingCode';

/**
 * Valor sentinela do 'Outro (digitar)'.
 *
 * Nao pode colidir com nome de armario de verdade — dai os dois-pontos, que o
 * cadastro aceita mas ninguem digita como nome de prateleira.
 */
const OTHER_STORAGE = '::outro';

export function RegisterPackagePage() {
  const queryClient = useQueryClient();
  const ocr = useLabelOcr();

  const [blockId, setBlockId] = useState('');
  const [unitId, setUnitId] = useState('');
  const [residentId, setResidentId] = useState('');
  const [residentName, setResidentName] = useState('');
  const [carrier, setCarrier] = useState('');
  const [trackingCode, setTrackingCode] = useState('');
  /**
   * Os rastreios das caixas 2..N. A caixa 1 e o `trackingCode` de sempre.
   *
   * Guardar so as EXTRAS, em vez de um array com todas, e o que mantem o leitor
   * de etiqueta funcionando sem saber que lote existe: ele continua escrevendo
   * em `trackingCode`, o selo "auto" continua marcando aquele campo, e aumentar
   * a quantidade depois de bipar nao move o codigo lido de lugar.
   */
  const [extraBoxes, setExtraBoxes] = useState<string[]>([]);
  const [storageLocation, setStorageLocation] = useState('');
  // Escapatoria do select: quando o lugar nao esta cadastrado, o campo volta a
  // ser texto. Fica no estado, e nao derivado da lista, porque o porteiro pode
  // querer digitar mesmo havendo opcoes.
  const [freeStorage, setFreeStorage] = useState(false);
  const [condition, setCondition] = useState<(typeof PACKAGE_CONDITIONS)[number]>('normal');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<ApiRequestError | null>(null);
  const [created, setCreated] = useState<CreatePackageResult | null>(null);
  // Segunda pergunta antes de gravar — ver o painel de conferencia mais abaixo.
  const [confirming, setConfirming] = useState(false);

  // --- Estado do leitor -----------------------------------------------------
  const [scanning, setScanning] = useState(false);
  const [auto, setAuto] = useState<Partial<Record<AutoField, boolean>>>({});
  const [scanNote, setScanNote] = useState<string | null>(null);
  const [choices, setChoices] = useState<UnitSuggestion[]>([]);
  // 'confirm' muda a cara da pergunta: em vez de uma fileira de opcoes
  // equivalentes, um palpite so com "sim / nao e esse". Ver `matchLabel`.
  const [choicesKind, setChoicesKind] = useState<'pick' | 'confirm'>('pick');

  // Listas completas: o casamento da etiqueta acontece contra elas, aqui no
  // dispositivo. Sem `search`, porque o texto a procurar so existe depois do
  // OCR e uma ida ao servidor por leitura tornaria o fluxo lento no balcao.
  const { data: units } = useQuery({
    queryKey: ['units', 'all'],
    queryFn: () => api.get<Page<Unit>>('/units?limit=500'),
    staleTime: 5 * 60_000,
  });

  const { data: residents } = useQuery({
    queryKey: ['residents', 'all'],
    queryFn: () => api.get<Page<Resident>>('/residents?limit=500'),
    staleTime: 5 * 60_000,
  });

  // So os ativos: a lista aqui e para escolher onde guardar AGORA, e armario
  // desativado continua existindo no historico sem voltar ao balcao.
  const { data: storageLocations } = useQuery({
    queryKey: ['storage-locations', 'ativos'],
    queryFn: () => api.get<{ items: StorageLocation[] }>('/armarios'),
    staleTime: 5 * 60_000,
  });

  const places = storageLocations?.items ?? [];
  const allUnits = units?.items ?? [];
  const selectedUnit = allUnits.find((unit) => unit.id === unitId);

  // Os blocos vem de `GET /blocks`, e nao das unidades ja carregadas: o bloco
  // existe antes da primeira unidade dele — ver `useBlocks`.
  const blocks = useBlocks(allUnits);
  const hasBlocks = blocks.length > 0;
  const semBloco = hasBlocks && allUnits.some((unit) => !unit.blockId);

  // Sem bloco escolhido a lista continua completa — trocar o bloco e um atalho
  // para encurtar a lista, nunca um passo obrigatorio a mais no balcao.
  const visibleUnits = blockId
    ? allUnits.filter((unit) =>
        blockId === 'sem-bloco' ? !unit.blockId : unit.blockId === blockId,
      )
    : allUnits;

  // Moradores da unidade escolhida, e so dela. Sem unidade a lista fica vazia
  // de proposito: oferecer os 478 moradores do condominio faria o porteiro
  // procurar um nome entre gente que nao tem relacao nenhuma com a caixa que
  // esta no balcao — e o erro de escolher o homonimo do bloco errado passaria
  // sem nada para segurar.
  const allResidents = residents?.items ?? [];
  const visibleResidents = unitId
    ? allResidents.filter((resident) =>
        resident.units.some((link) => link.unitId === unitId),
      )
    : [];

  // `nextCursor` preenchido significa que o `limit=500` cortou a lista. Com 478
  // moradores no primeiro cliente real, isso esta a poucas dezenas de cadastros
  // de acontecer — e um seletor que omite gente calado e pior do que nenhum.
  const residentsTruncated = residents?.nextCursor != null;

  /**
   * O que o painel de conferencia repete em texto.
   *
   * O destinatario e resolvido AQUI, e nao deixado como "morador principal da
   * unidade": esse rotulo e verdadeiro e inutil no balcao — ninguem confere um
   * nome que a tela nao mostra. A regra e a mesma do backend
   * (`ORDER BY ur.is_primary DESC, r.name LIMIT 1`), entao o nome exibido e o
   * nome que vai receber a mensagem.
   */
  const impliedResident = residentId
    ? visibleResidents.find((resident) => resident.id === residentId)
    : (visibleResidents.find((resident) =>
        resident.units.some((link) => link.unitId === unitId && link.isPrimary),
      ) ??
      [...visibleResidents].sort((a, b) => a.name.localeCompare(b.name))[0]);

  const boxCount = 1 + extraBoxes.length;

  const confirmSummary = {
    unit: selectedUnit
      ? [selectedUnit.block, selectedUnit.label].filter(Boolean).join(' • ')
      : '—',
    recipient: impliedResident
      ? impliedResident.phoneE164
        ? impliedResident.name
        : `${impliedResident.name} — sem WhatsApp cadastrado, nenhum aviso sai`
      : 'Ninguem: a unidade nao tem morador cadastrado, nenhum aviso sai',
  };

  function markAuto(field: AutoField) {
    setAuto((current) => ({ ...current, [field]: true }));
  }

  function clearAuto(field: AutoField) {
    setAuto((current) => ({ ...current, [field]: false }));
  }

  function applySuggestion(suggestion: UnitSuggestion) {
    setUnitId(suggestion.unitId);
    // O bloco acompanha a unidade lida: deixar o filtro em outro bloco
    // esconderia do porteiro justamente a unidade que a camera acabou de
    // escolher.
    const unit = allUnits.find((item) => item.id === suggestion.unitId);
    if (unit) setBlockId(unit.blockId ?? (hasBlocks ? 'sem-bloco' : ''));
    markAuto('unitId');
    setResidentId(suggestion.residentId ?? '');
    setResidentName(suggestion.residentName ?? '');
    setChoices([]);
  }

  /** Resposta a uma pergunta da leitura — a nota vira o resultado dela. */
  function answerWith(suggestion: UnitSuggestion) {
    applySuggestion(suggestion);
    setScanNote(
      suggestion.residentName
        ? `Preenchido: ${suggestion.residentName} — ${[suggestion.block, suggestion.unitLabel]
            .filter(Boolean)
            .join(' • ')}.`
        : `Unidade preenchida: ${[suggestion.block, suggestion.unitLabel]
            .filter(Boolean)
            .join(' • ')}.`,
    );
  }

  // --- Codigo de barras -----------------------------------------------------
  const handleDecode = useCallback((raw: string) => {
    const decoded = decodeScan(raw);

    if (decoded.kind === 'pickup') {
      setScanNote('Esse e o QR de retirada de uma encomenda ja registrada — use a tela Ler QR.');
      return;
    }

    setTrackingCode(decoded.value);
    markAuto('trackingCode');

    // A transportadora so e preenchida quando o FORMATO do codigo garante
    // qual e. Chutar aqui encheria o historico de transportadora errada.
    if (decoded.carrier) {
      setCarrier(decoded.carrier);
      markAuto('carrier');
    }

    setScanNote(`${describeScan(decoded)}: ${decoded.value}`);
    navigator.vibrate?.(60);
  }, []);

  // --- OCR da etiqueta ------------------------------------------------------
  const handleCaptureText = useCallback(
    async (canvas: HTMLCanvasElement) => {
      const text = await ocr.read(canvas);
      if (!text) {
        setScanNote('Nao consegui ler o texto. Aproxime a camera e evite sombra sobre a etiqueta.');
        return;
      }

      const hints = parseLabelText(text);

      if (hints.trackingCode && !trackingCode) {
        setTrackingCode(hints.trackingCode);
        markAuto('trackingCode');
      }
      if (hints.carrier && !carrier) {
        setCarrier(hints.carrier);
        markAuto('carrier');
      }

      const match = matchLabel(hints, text, units?.items ?? [], residents?.items ?? []);

      if (match.suggestion) {
        applySuggestion(match.suggestion);
        setScanNote(`Unidade preenchida: ${match.suggestion.reason}.`);
        navigator.vibrate?.(60);
        return;
      }

      if (match.candidates.length > 0) {
        setChoices(match.candidates);
        setChoicesKind(match.ask === 'confirm' ? 'confirm' : 'pick');
        setScanNote(
          match.ask === 'confirm'
            ? // Palpite raso — so parte do nome bateu, ou bateu perdoando erro
              // de leitura. Vale mostrar como pergunta: um toque contra
              // digitar a unidade e o morador inteiros.
              'So parte do nome da etiqueta bateu. Esse e o destinatario?'
            : 'Mais de uma opcao combina com a etiqueta. Escolha qual e:',
        );
        return;
      }

      setScanNote(
        hints.unitNumber
          ? `Li "${hints.unitNumber}" na etiqueta, mas nao ha unidade com esse numero. Selecione abaixo.`
          : 'Nao encontrei a unidade na etiqueta. Selecione abaixo.',
      );
    },
    [ocr, trackingCode, carrier, units, residents],
  );

  const mutation = useMutation({
    mutationFn: () => {
      // UMA caixa e uma encomenda simples, e vai no formato de sempre: sem
      // `items`. Cada estado tem uma representacao so — a API recusa `items`
      // com um item pelo mesmo motivo.
      const items: CreatePackageItem[] | undefined =
        boxCount > 1
          ? [
              { trackingCode: trackingCode || undefined },
              ...extraBoxes.map((code) => ({ trackingCode: code || undefined })),
            ]
          : undefined;

      return api.post<CreatePackageResult>('/packages', {
        unitId,
        residentId: residentId || undefined,
        carrier,
        // Em lote o rastreio e de cada caixa e viaja dentro de `items`; manda-lo
        // tambem no topo e 422 na API, de proposito.
        trackingCode: items ? undefined : trackingCode || undefined,
        storageLocation,
        condition,
        notes: notes || undefined,
        items,
      });
    },
    onSuccess: (result) => {
      setCreated(result);
      setError(null);
      setConfirming(false);
      // Contadores da visao geral e listagem ficaram velhos.
      void queryClient.invalidateQueries({ queryKey: ['packages'] });

      // Limpa o que muda de pacote para pacote e mantem a unidade: chegou uma
      // caixa, e comum vir outra da mesma unidade na mesma remessa.
      setCarrier('');
      setTrackingCode('');
      // A quantidade volta a 1: o proximo carrinho tem o tamanho que tiver, e
      // herdar "3 caixas" da remessa anterior gravaria tres registros onde
      // chegou um pacote.
      setExtraBoxes([]);
      setStorageLocation('');
      // Volta ao select: quem digitou um lugar avulso uma vez nao vai querer
      // digitar de novo na proxima caixa da mesma remessa.
      setFreeStorage(false);
      setNotes('');
      setAuto({});
      setScanNote(null);
    },
    onError: (err) => {
      if (err instanceof ApiRequestError) setError(err);
      // Volta ao formulario: o erro quase sempre e sobre um campo (unidade,
      // morador que nao mora ali, etiqueta repetida) e a correcao e ali.
      setConfirming(false);
    },
  });

  /**
   * O submit NAO grava. Ele abre a conferencia.
   *
   * Registrar dispara um WhatsApp para um morador de verdade, e o que sai
   * errado com mais frequencia — bloco trocado, homonimo do andar de cima — e
   * exatamente o que um formulario preenchido pela camera esconde: o campo
   * parece certo porque alguem (ou algo) ja o preencheu. O painel repete em
   * texto quem vai receber a mensagem, que e a unica coisa que o porteiro
   * consegue conferir olhando para a caixa na mao.
   */
  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!confirming) {
      setError(null);
      setConfirming(true);
      return;
    }
    mutation.mutate();
  }

  // Qualquer mexida no formulario derruba a conferencia: confirmar uma tela
  // que descreve outro estado seria pior do que nao ter conferencia nenhuma.
  useEffect(() => {
    setConfirming(false);
  }, [
    unitId,
    residentId,
    blockId,
    carrier,
    trackingCode,
    extraBoxes,
    storageLocation,
    condition,
    notes,
  ]);

  return (
    <>
      <PageHeader title="Registrar encomenda" />

      {/* Coluna fixa para a previa: ela e uma referencia do tamanho de um
          celular e nao ganha nada esticando junto com o formulario. */}
      <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_25rem]">
        <Card className="p-4 sm:p-6">
          {error?.isPlanBlock && (
            <div className="mb-5">
              <PlanBlock message={error.message} />
            </div>
          )}

          {/* ----------------------------------------------------------------
              Passo 1 — leitor. Antes do formulario porque e ele que deveria
              preencher o formulario.
          ---------------------------------------------------------------- */}
          <section className="rounded-card border border-line bg-surface-2 p-3 sm:p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span className="flex items-center gap-2 text-sm font-bold">
                <IconBarcode className="size-5 text-accent" />
                Ler etiqueta
              </span>
              <Button
                variant={scanning ? 'ghost' : 'primary'}
                className="py-2"
                onClick={() => {
                  setScanning((open) => !open);
                  setScanNote(null);
                }}
              >
                {scanning ? 'Fechar camera' : 'Abrir camera'}
              </Button>
            </div>

            {scanning && (
              <CameraScanner
                active
                onDecode={handleDecode}
                onCaptureText={(canvas) => void handleCaptureText(canvas)}
                captureBusy={ocr.status === 'loading' || ocr.status === 'reading'}
                captureLabel={
                  ocr.status === 'loading' ? 'Preparando leitor...' : 'Ler destinatario'
                }
                hint="O codigo de barras entra sozinho. Para o apartamento, enquadre a etiqueta e toque em Ler destinatario."
                className="mt-4"
              />
            )}

            {/* Barra de progresso do download do idioma — sao alguns MB na
                primeira leitura do dispositivo, e silencio aqui parece
                travamento. */}
            {ocr.status === 'loading' && (
              <div className="mt-3 h-1 overflow-hidden rounded-pill bg-surface">
                <div
                  className="h-full rounded-pill bg-accent transition-all"
                  style={{ width: `${Math.max(ocr.progress * 100, 5)}%` }}
                />
              </div>
            )}

            {scanNote && <p className="mt-3 text-sm text-muted">{scanNote}</p>}

            {choices.length > 0 && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {choices.map((choice) => (
                  <button
                    key={`${choice.unitId}-${choice.residentId ?? ''}`}
                    type="button"
                    onClick={() => answerWith(choice)}
                    className={cx(
                      'rounded-pill border px-3 py-1.5 text-sm font-semibold transition',
                      // Num palpite unico o botao E a resposta "sim" — ele
                      // aparece ja destacado para nao ficar com a mesma cara
                      // de uma opcao entre varias.
                      choicesKind === 'confirm'
                        ? 'border-accent/40 bg-accent/10 text-accent hover:bg-accent/15'
                        : 'border-line bg-surface hover:border-accent hover:text-accent',
                    )}
                  >
                    {choice.reason}
                  </button>
                ))}

                {/* Sem esta saida a pergunta fica na tela sem resposta possivel
                    alem de acertar: "nao" precisa ser tao clicavel quanto "sim". */}
                {choicesKind === 'confirm' && (
                  <button
                    type="button"
                    onClick={() => {
                      setChoices([]);
                      setScanNote('Certo — escolha o morador abaixo.');
                    }}
                    className="rounded-pill border border-line px-3 py-1.5 text-sm font-semibold text-muted transition hover:text-fg"
                  >
                    Nao e ele
                  </button>
                )}
              </div>
            )}
          </section>

          <form className="mt-6 space-y-5" onSubmit={handleSubmit}>
            {/* Uma grid so para os campos curtos. A ordem e a do balcao: para
                onde vai (bloco, unidade), quem trouxe (transportadora,
                rastreio) e onde ficou guardado (local, condicao). */}
            <div className="grid gap-4 sm:grid-cols-2">
              {hasBlocks && (
                <Field label="Bloco">
                  <Select
                    value={blockId}
                    // Busca sempre, e nao so a partir de 8 opcoes: no balcao,
                    // com a caixa na mao, digitar "C" e mais rapido do que
                    // rolar a lista mesmo quando ela e curta.
                    searchable
                    emptyOption="Todos os blocos"
                    options={[
                      ...blocks.map(([id, name]) => ({ value: id, label: name })),
                      ...(semBloco ? [{ value: 'sem-bloco', label: 'Sem bloco' }] : []),
                    ]}
                    onChange={(next) => {
                      setBlockId(next);

                      // Trocar de bloco com uma unidade de outro bloco ainda
                      // selecionada e o erro que este campo existe para evitar:
                      // a unidade some da lista mas continua no formulario.
                      const stillVisible = allUnits.some(
                        (unit) =>
                          unit.id === unitId &&
                          (next === ''
                            ? true
                            : next === 'sem-bloco'
                              ? !unit.blockId
                              : unit.blockId === next),
                      );
                      if (!stillVisible) {
                        setUnitId('');
                        setResidentId('');
                        setResidentName('');
                        clearAuto('unitId');
                      }
                    }}
                  />
                </Field>
              )}

              <Field
                label={
                  <>
                    Unidade
                    {auto.unitId && (
                      <AutoBadge
                        onClear={() => {
                          setUnitId('');
                          setResidentId('');
                          setResidentName('');
                          clearAuto('unitId');
                        }}
                      />
                    )}
                  </>
                }
                error={error?.fields.unitId}
              >
                <Select
                  value={unitId}
                  searchable
                  onChange={(next) => {
                    setUnitId(next);
                    clearAuto('unitId');
                    setResidentId('');
                    setResidentName('');
                  }}
                  options={visibleUnits.map((unit) => ({
                    value: unit.id,
                    // Com um bloco escolhido o prefixo seria a mesma letra
                    // repetida em toda a lista — so atrapalha a leitura.
                    label: !blockId && unit.block ? `${unit.block} • ${unit.label}` : unit.label,
                    hint: unit.residentCount === 0 ? 'sem morador' : undefined,
                  }))}
                  className={cx(auto.unitId && '[&>button]:border-accent/60')}
                />
              </Field>

              {/* Destinatario logo depois da unidade: e a mesma pergunta ("para
                  quem vai") em dois graus de precisao, e a lista aqui so existe
                  depois que a unidade responde o primeiro. */}
              <Field label="Destinatario">
                <Select
                  value={residentId}
                  searchable
                  disabled={!unitId}
                  // Vazio nao e "ninguem": o backend cai no morador principal
                  // da unidade, que e o comportamento certo por padrao e o que
                  // o produto sempre fez. O rotulo precisa dizer isso, senao o
                  // porteiro escolhe um nome so para nao deixar em branco.
                  // Sem unidade nao ha "morador principal" de coisa nenhuma: a
                  // opcao vazia so existe depois que a unidade responde, senao
                  // o campo desligado mostraria um padrao que nao se aplica.
                  emptyOption={unitId ? 'Morador principal da unidade' : undefined}
                  placeholder={unitId ? 'Morador principal da unidade' : 'Escolha a unidade antes'}
                  options={visibleResidents.map((resident) => ({
                    value: resident.id,
                    label: resident.name,
                    hint: resident.phoneE164 ? undefined : 'sem WhatsApp',
                  }))}
                  onChange={(next) => {
                    setResidentId(next);
                    // O nome vindo do OCR era so um rotulo de leitura. A partir
                    // do momento em que alguem escolhe na mao, quem manda e a
                    // escolha — manter os dois na tela mostraria dois
                    // destinatarios diferentes ao mesmo tempo.
                    setResidentName('');
                  }}
                />
              </Field>

              <Field
                label={
                  <>
                    Transportadora
                    {auto.carrier && (
                      <AutoBadge
                        onClear={() => {
                          setCarrier('');
                          clearAuto('carrier');
                        }}
                      />
                    )}
                  </>
                }
              >
                <TextInput
                  value={carrier}
                  onChange={(event) => {
                    setCarrier(event.target.value);
                    clearAuto('carrier');
                  }}
                  placeholder="Mercado Livre"
                  className={cx(auto.carrier && 'border-accent/60')}
                />
              </Field>

              <Field
                label={
                  <>
                    {boxCount > 1 ? 'Rastreio da caixa 1' : 'Codigo de rastreio'}
                    {auto.trackingCode && (
                      <AutoBadge
                        onClear={() => {
                          setTrackingCode('');
                          clearAuto('trackingCode');
                        }}
                      />
                    )}
                  </>
                }
              >
                <TextInput
                  value={trackingCode}
                  onChange={(event) => {
                    setTrackingCode(event.target.value.toUpperCase());
                    clearAuto('trackingCode');
                  }}
                  placeholder="Lido do codigo de barras"
                  spellCheck={false}
                  className={cx('font-mono text-sm', auto.trackingCode && 'border-accent/60')}
                />
              </Field>

              {/* ----------------------------------------------------------------
                  Quantos volumes sao.
                  Tres caixas do mesmo destinatario no mesmo carrinho custavam
                  tres registros, tres avisos e tres retiradas — o mesmo morador
                  digitado tres vezes e tres WhatsApp em sequencia por uma
                  entrega so. Este campo e o que transforma isso em um registro,
                  um aviso e uma retirada.
                  Comeca em 1 e o formulario nao muda de forma enquanto ficar ali:
                  quem nunca recebe carrinho cheio nao ganhou um passo a mais.
              ---------------------------------------------------------------- */}
              <Field label="Volumes" className="sm:col-span-2">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-1 rounded-2xl border border-line bg-surface-2 p-1">
                    <Button
                      type="button"
                      variant="quiet"
                      className="px-3 py-1.5"
                      disabled={boxCount <= 1}
                      aria-label="Menos um volume"
                      onClick={() => setExtraBoxes((current) => current.slice(0, -1))}
                    >
                      −
                    </Button>
                    <span
                      className="min-w-10 text-center text-lg font-bold tabular-nums"
                      aria-live="polite"
                    >
                      {boxCount}
                    </span>
                    <Button
                      type="button"
                      variant="quiet"
                      className="px-3 py-1.5"
                      disabled={boxCount >= MAX_PACKAGE_BATCH}
                      aria-label="Mais um volume"
                      onClick={() => setExtraBoxes((current) => [...current, ''])}
                    >
                      +
                    </Button>
                  </div>

                  <p className="min-w-0 flex-1 text-sm text-muted">
                    {boxCount > 1
                      ? `Um registro, um aviso e uma retirada para as ${boxCount} caixas. O plano conta ${boxCount} encomendas.`
                      : 'Chegou mais de uma caixa para o mesmo morador? Aumente aqui.'}
                  </p>
                </div>

                {/* O rastreio nao se repete entre as caixas de proposito: cada
                    volume vem com o seu, e copiar um em todos gravaria que os
                    tres sao o mesmo objeto. Fica em branco quando a portaria nao
                    tem a etiqueta de algum deles — e opcional, como sempre foi. */}
                {extraBoxes.length > 0 && (
                  <ul className="mt-3 space-y-2">
                    {extraBoxes.map((code, index) => (
                      // eslint-disable-next-line react/no-array-index-key -- a
                      // posicao E a identidade aqui: "caixa 2" e a segunda linha.
                      <li key={index} className="flex items-center gap-2">
                        <span className="w-16 shrink-0 text-sm font-semibold text-muted">
                          Caixa {index + 2}
                        </span>
                        <TextInput
                          value={code}
                          onChange={(event) =>
                            setExtraBoxes((current) =>
                              current.map((item, position) =>
                                position === index ? event.target.value.toUpperCase() : item,
                              ),
                            )
                          }
                          placeholder="Rastreio (opcional)"
                          spellCheck={false}
                          className="min-w-0 flex-1 font-mono text-sm"
                        />
                        <Button
                          type="button"
                          variant="quiet"
                          className="shrink-0 py-2"
                          aria-label={`Remover a caixa ${index + 2}`}
                          onClick={() =>
                            setExtraBoxes((current) =>
                              current.filter((_, position) => position !== index),
                            )
                          }
                        >
                          <IconClose className="size-4" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </Field>

              {/* Escolher, e nao digitar — mas sem fechar a porta.
                  Cadastrando os armarios, o balcao para de produzir "Armario A
                  - 03", "armario a 03" e "ARM A3" como se fossem tres lugares.
                  Sem nenhum cadastrado, o campo continua exatamente como era:
                  quem nunca abriu a tela de armarios nao perde nada. */}
              <Field label="Local de armazenamento">
                {places.length > 0 && !freeStorage ? (
                  <Select
                    value={storageLocation}
                    onChange={(next) => {
                      if (next === OTHER_STORAGE) {
                        setFreeStorage(true);
                        setStorageLocation('');
                      } else {
                        setStorageLocation(next);
                      }
                    }}
                    emptyOption="Nao definido"
                    options={[
                      ...places.map((place) => ({ value: place.name, label: place.name })),
                      { value: OTHER_STORAGE, label: 'Outro (digitar)' },
                    ]}
                  />
                ) : (
                  <div className="flex items-center gap-2">
                    <TextInput
                      value={storageLocation}
                      onChange={(event) => setStorageLocation(event.target.value)}
                      placeholder="Armario A - 03"
                      maxLength={80}
                      className="min-w-0 flex-1"
                    />
                    {places.length > 0 && (
                      <Button
                        type="button"
                        variant="quiet"
                        className="shrink-0 py-2"
                        onClick={() => {
                          setFreeStorage(false);
                          setStorageLocation('');
                        }}
                      >
                        Ver lista
                      </Button>
                    )}
                  </div>
                )}
              </Field>

              {/* Sem o campo de bloco sobra um slot na ultima linha: a condicao
                  ocupa a largura toda em vez de deixar meio buraco. */}
              <Field label="Condicao do pacote" className={cx(!hasBlocks && 'sm:col-span-2')}>
                <Select
                  value={condition}
                  onChange={(next) => setCondition(next as (typeof PACKAGE_CONDITIONS)[number])}
                  options={PACKAGE_CONDITIONS.map((value) => ({
                    value,
                    label: PACKAGE_CONDITION_LABELS[value],
                  }))}
                />
              </Field>
            </div>

            {/* Destinatario so aparece quando o leitor identificou alguem: sem
                isso, o backend usa o morador principal da unidade, que e o
                comportamento certo por padrao. */}
            {/* O seletor de morador le uma lista carregada de uma vez, com
                teto de 500. Passando disso ele deixaria de mostrar gente que
                existe — e um campo que omite calado e pior do que um campo que
                nao existe. Enquanto nao ha busca no servidor, pelo menos o
                porteiro fica sabendo. */}
            {residentsTruncated && (
              <Alert tone="warn">
                Ha mais moradores cadastrados do que cabe nesta lista. Se nao achar o
                destinatario, registre na unidade — o morador principal recebe o aviso.
              </Alert>
            )}

            {/* So quando o OCR leu um nome que NAO casou com ninguem da
                unidade. Casando, ele ja aparece escolhido no campo acima, e
                repetir aqui mostraria dois destinatarios ao mesmo tempo. */}
            {residentName && !residentId && (
              <p className="flex items-center gap-2 text-sm text-muted">
                Nome lido na etiqueta:
                <span className="font-semibold text-fg">{residentName}</span>
                <span className="text-faint">— nao esta cadastrado nesta unidade</span>
                <button
                  type="button"
                  onClick={() => {
                    setResidentId('');
                    setResidentName('');
                  }}
                  className="text-faint hover:text-fg"
                  aria-label="Remover destinatario identificado"
                >
                  <IconClose className="size-4" />
                </button>
              </p>
            )}

            {/* A foto saiu daqui. Este bloco era uma area de arrastar que nao
                aceitava nada — clicar nele nunca fez coisa alguma. O upload de
                verdade acontece DEPOIS de registrar (`PackagePhoto`), porque a
                encomenda so tem id quando ja foi gravada. */}

            <Field label="Observacao">
              <textarea
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                rows={3}
                placeholder="Opcional"
                className="w-full rounded-2xl border border-line bg-surface-2 px-4 py-3 text-fg placeholder:text-faint focus:border-accent/60 focus:outline-none"
              />
            </Field>

            {error && !error.isPlanBlock && <Alert>{error.message}</Alert>}

            {/* ----------------------------------------------------------------
                Conferencia. Nao e um `confirm()` do navegador, pelo mesmo
                motivo de sempre neste projeto: o dialogo nativo nao consegue
                dizer PARA QUEM a mensagem vai — e essa e a unica informacao
                que faz alguem parar antes de confirmar.
            ---------------------------------------------------------------- */}
            {confirming && (
              <div className="rounded-2xl border border-accent/40 bg-accent/5 p-4">
                <p className="text-sm font-bold">Confere antes de registrar</p>

                <dl className="mt-3 space-y-1.5 text-sm">
                  <div className="flex gap-2">
                    <dt className="w-32 shrink-0 text-muted">Unidade</dt>
                    <dd className="font-bold">
                      {confirmSummary.unit}
                    </dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="w-32 shrink-0 text-muted">Avisar</dt>
                    <dd className="font-bold">{confirmSummary.recipient}</dd>
                  </div>
                  {carrier && (
                    <div className="flex gap-2">
                      <dt className="w-32 shrink-0 text-muted">Transportadora</dt>
                      <dd>{carrier}</dd>
                    </div>
                  )}
                  {boxCount > 1 && (
                    <div className="flex gap-2">
                      <dt className="w-32 shrink-0 text-muted">Volumes</dt>
                      <dd className="font-bold">{boxCount} caixas</dd>
                    </div>
                  )}
                  {trackingCode && (
                    <div className="flex gap-2">
                      <dt className="w-32 shrink-0 text-muted">Rastreio</dt>
                      <dd className="break-all">
                        {boxCount > 1 ? `caixa 1: ${trackingCode}` : trackingCode}
                      </dd>
                    </div>
                  )}
                  {storageLocation && (
                    <div className="flex gap-2">
                      <dt className="w-32 shrink-0 text-muted">Guardada em</dt>
                      <dd>{storageLocation}</dd>
                    </div>
                  )}
                  {condition !== 'normal' && (
                    <div className="flex gap-2">
                      <dt className="w-32 shrink-0 text-muted">Condicao</dt>
                      <dd className="font-bold text-warn">
                        {PACKAGE_CONDITION_LABELS[condition]}
                      </dd>
                    </div>
                  )}
                </dl>

                <p className="mt-3 text-sm text-muted">
                  Ao confirmar, o WhatsApp com o QR de retirada entra na fila.
                  {boxCount > 1 &&
                    ' Um QR so, valido pelas ' + boxCount + ' caixas — e o plano conta ' + boxCount + ' encomendas.'}
                  {' '}Registrou errado? Da para cancelar depois, na lista de encomendas.
                </p>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <Button type="submit" disabled={!unitId} loading={mutation.isPending}>
                {confirming
                  ? 'Confirmar registro'
                  : boxCount > 1
                    ? `Registrar ${boxCount} caixas e gerar QR`
                    : 'Registrar e gerar QR'}
              </Button>

              {confirming && (
                <Button type="button" variant="ghost" onClick={() => setConfirming(false)}>
                  Corrigir
                </Button>
              )}
            </div>
          </form>
        </Card>

        {/* Previa da notificacao */}
        <Card className="p-5 sm:p-6">
          <p className="eyebrow">Previa da notificacao</p>
          <h2 className="mt-2 text-2xl font-bold tracking-tight">Como o morador recebe</h2>

          <div className="mt-6 rounded-card border border-line bg-surface-2 p-5">
            <div className="flex items-center gap-3">
              <span className="grid size-9 place-items-center rounded-full bg-accent text-sm font-extrabold text-on-accent">
                A
              </span>
              <span>
                <span className="block text-sm font-bold">Azko Condos</span>
                <span className="block text-xs text-muted">Atendimento automatico</span>
              </span>
            </div>

            <div className="mt-5 rounded-2xl border border-line bg-ink p-4">
              <div className="grid h-32 place-items-center rounded-xl border border-line text-xs uppercase tracking-wider text-faint">
                Foto da encomenda
              </div>

              <p className="mt-4 font-bold">
                {boxCount > 1 ? `📦 Chegaram ${boxCount} encomendas!` : '📦 Sua encomenda chegou!'}
              </p>
              <p className="mt-1 text-sm text-muted">
                Ola. Recebemos {boxCount > 1 ? `${boxCount} encomendas` : 'uma encomenda'} para a
                unidade {created?.package.unitLabel ?? selectedUnit?.label ?? '—'}. Apresente o QR
                Code abaixo na recepcao para retirar.
              </p>

              <div className="mt-4 grid place-items-center">
                <PickupQr url={created?.pickupUrl ?? null} />
              </div>

              {/* "Uso unico" deixou de ser verdade no lote com retirada parcial:
                  o token so morre quando nao sobra caixa nenhuma, senao o
                  morador que levou duas de tres perderia o acesso a terceira. */}
              <p className="mt-3 text-center text-xs text-faint">
                {boxCount > 1
                  ? `QR exclusivo • vale pelas ${boxCount} caixas`
                  : 'QR exclusivo • uso unico'}
              </p>
            </div>
          </div>

          {created && (
            <>
              <p className="mt-4 text-sm text-accent">
                {created.package.boxCount > 1
                  ? `Registro #${created.package.code} com ${created.package.boxCount} caixas e notificacao enfileirada.`
                  : `Encomenda #${created.package.code} registrada e notificacao enfileirada.`}
              </p>
              <PackagePhoto packageId={created.package.id} code={created.package.code} />
            </>
          )}
        </Card>
      </div>
    </>
  );
}

/**
 * QR de retirada de verdade.
 *
 * Renderizado no cliente a partir da URL que a API devolve: o servidor nunca
 * precisa produzir nem guardar imagem, e o token continua existindo em um
 * lugar so — o hash no banco. Fundo branco fixo porque leitor de celular tem
 * dificuldade com QR invertido, mesmo em tela escura.
 */
function PickupQr({ url }: { url: string | null }) {
  const [dataUrl, setDataUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!url) {
      setDataUrl(null);
      return;
    }

    let cancelled = false;
    void import('qrcode').then(async (QRCode) => {
      const image = await QRCode.toDataURL(url, {
        margin: 1,
        width: 320,
        errorCorrectionLevel: 'M',
        color: { dark: '#000000', light: '#ffffff' },
      });
      if (!cancelled) setDataUrl(image);
    });

    return () => {
      cancelled = true;
    };
  }, [url]);

  if (!dataUrl) {
    return (
      <div className="grid size-40 place-items-center rounded-xl border border-line text-center text-xs text-faint">
        QR gerado ao registrar
      </div>
    );
  }

  return (
    <img
      src={dataUrl}
      alt="QR Code de retirada da encomenda"
      className="size-40 rounded-xl bg-white p-2"
    />
  );
}
